/**
 * @file busy-mixin.ts
 *
 * Manages a ref-counted busy state for the app. Branch components signal
 * intent via `as-app-busy-request` events with `{ key: string, busy: boolean }`.
 * This mixin maintains a Set of active keys and emits `as-app-busy` with
 * `{ busy: boolean }` only when the aggregate state transitions (0→1 or 1→0).
 *
 * This prevents any single component from clearing a busy state that was set
 * by a different operation running concurrently.
 */
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by BusyMixin. (No public surface - signal-based) */
export interface BusyMixinMembers {}

export const BusyMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class BusyMixin extends Base {
    private _busyKeys = new Set<string>();

    constructor(...args: any[]) {
      super(...args);
      window.addEventListener('as-app-busy-request', this._handleBusyRequest as EventListener);
      window.addEventListener('as-get-app-busy', this._handleGetBusy as EventListener);
    }

    private _handleGetBusy = (ev: CustomEvent<{ resolve: (detail: { busy: boolean; keys: string[] }) => void }>): void => {
      ev.detail.resolve({
        busy: this._busyKeys.size > 0,
        keys: Array.from(this._busyKeys)
      });
    };

    private _handleBusyRequest = (ev: CustomEvent<{ key: string; busy: boolean }>): void => {
      const { key, busy } = ev.detail;
      const wasBusy = this._busyKeys.size > 0;
      if (busy) {
        this._busyKeys.add(key);
      } else {
        this._busyKeys.delete(key);
      }
      const isBusy = this._busyKeys.size > 0;
      
      // Emit if busy status changed OR if we are currently busy (to update keys) 
      // OR if it just became idle (to clear keys).
      if (isBusy !== wasBusy || isBusy || wasBusy) {
        window.dispatchEvent(new CustomEvent('as-app-busy', { 
          detail: { 
            busy: isBusy,
            keys: Array.from(this._busyKeys)
          } 
        }));
      }
    };

    async connectedCallback() {
      await super.connectedCallback?.();
    }

    disconnectedCallback() {
      window.removeEventListener('as-app-busy-request', this._handleBusyRequest as EventListener);
      window.removeEventListener('as-get-app-busy', this._handleGetBusy as EventListener);
      super.disconnectedCallback?.();
    }
  };
